"use client";

import type {
	WalletSessionOptions,
	WalletSession as WalletSessionType,
} from "@whop/embedded-components-vanilla-js/types";
import type { PropsWithChildren } from "react";

import React, {
	createContext,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
} from "react";
import { useElements } from "../provider";

interface ContextState {
	walletSession: WalletSessionType | null;
}

const WalletSessionContext = createContext<ContextState | null>(null);

export function WalletSession({
	children,
	token,
	companyId,
	currency,
	ref,
}: PropsWithChildren<
	WalletSessionOptions & {
		ref?: React.RefObject<{
			walletSession: WalletSessionType | null;
		} | null>;
	}
>) {
	const elements = useElements();

	const walletSession = useMemo(() => {
		if (!elements) return null;
		return elements.createWalletSession({ token, companyId, currency });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- session created once per elements instance
	}, [elements]);

	useEffect(() => {
		if (!walletSession) return;
		walletSession.updateOptions({ token, companyId, currency });
	}, [walletSession, token, companyId, currency]);

	const value: ContextState = useMemo(
		() => ({
			walletSession,
		}),
		[walletSession],
	);

	useImperativeHandle(ref, () => ({ walletSession }), [walletSession]);

	return (
		<WalletSessionContext.Provider value={value}>
			{children}
		</WalletSessionContext.Provider>
	);
}

export function useWalletSession() {
	const ctx = useContext(WalletSessionContext);
	if (!ctx) {
		throw new Error(
			"useWalletSession must be used within a WalletSessionProvider",
		);
	}
	return ctx.walletSession;
}
